import React, {
  FormEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import without from 'lodash/without';
import {
  isDescriptionLengthValid,
  RandomId,
  ALLOWED_MEDIA_TYPE_MIMES,
  DashValidation,
  isEmptyOrNil,
} from 'dash-common';

import {
  Form,
  FormErrorTypes,
  Textarea,
  useFormState,
  useFormValidation,
  useServerResponseTranslation,
  FileList,
  FileInput,
  Button,
  Flex,
  PaginationContext,
} from 'dash-ui';

import { draftFileUploadMutation } from 'gql/file/mutations';
import {
  useAddNoteToPropertyMutation,
  useEditPropertyNoteMutation,
  GetPropertyNotesDocument,
  PropertyNote,
} from 'gql/generated.graphql';

enum FormFields {
  Message = 'message',
  Files = 'files',
}

const FormFieldsList = [FormFields.Message, FormFields.Files];

type Props = {
  propertyId: string;
  propertyNote?: PropertyNote;
  onCancel?: () => void;
};

function SetNoteForm({ propertyId, propertyNote, onCancel }: Props) {
  const { t } = useTranslation('forms');
  const translateServerResponse = useServerResponseTranslation();
  const [formErrors, , [setMessageError], [resetFormErrors]] =
    useFormValidation(FormFieldsList);
  const [
    formState,
    [setMessage, setFiles],
    [formOnSubmit, formOnError, , setFormValues, resetForm],
  ] = useFormState(FormFieldsList);
  const { state: paginationState, getRefetchProps } =
    useContext(PaginationContext);
  const { skip, limit } = getRefetchProps(paginationState);
  const { files, message } = formState;
  const [attachedFilesIds, setAttachedFilesIds] = useState<string[] | null>(
    null
  );
  const [isFilesUploading, setFileUploading] = useState(false);
  const isEditMode = Boolean(propertyNote);

  const [draftFileUpload] = useMutation(draftFileUploadMutation);
  const [addNoteToPropertyMutation, { loading }] = useAddNoteToPropertyMutation(
    {
      refetchQueries: [
        {
          query: GetPropertyNotesDocument,
          variables: {
            propertyId,
            pagination: { skip, limit },
          },
        },
      ],
    }
  );

  const [editPropertyNoteMutation, { loading: updateLoading }] =
    useEditPropertyNoteMutation({
      refetchQueries: [
        {
          query: GetPropertyNotesDocument,
          variables: {
            propertyId,
          },
        },
      ],
    });

  useEffect(() => {
    if (propertyNote) {
      setFormValues({
        message: propertyNote.message,
        files: propertyNote.attachedFiles,
      });
      setAttachedFilesIds(propertyNote.attachedFilesIds);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyNote]);

  const validMessage = (value: string) => {
    if (isEmptyOrNil(value)) {
      setMessageError({
        type: FormErrorTypes.Info,
        message: t`messages.noteBlank`,
      });
      return false;
    }
    if (!isDescriptionLengthValid(value)) {
      setMessageError({
        type: FormErrorTypes.Invalid,
        message: t('messages.noteInvalid', {
          length: DashValidation.MaxDescriptionLength,
        }),
      });
      return false;
    }
    setMessageError(null);
    return true;
  };

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();
    formOnSubmit();
    const { message } = formState;

    if (!validMessage(message)) {
      return null;
    }

    const toUploadList = files
      ? files.filter((file: { toUpload: File }) => Boolean(file.toUpload))
      : [];
    setFileUploading(true);
    Promise.all(
      toUploadList.map(({ toUpload: file }: { toUpload: File }) =>
        draftFileUpload({
          variables: { input: { file } },
          context: {
            fetchOptions: {
              useUpload: true,
            },
          },
        })
      )
    ) // @ts-ignore
      .then((uploadedList) => {
        setFileUploading(false);
        return !propertyNote
          ? addNoteToPropertyMutation({
              variables: {
                message,
                propertyId,
                attachedFilesIds: uploadedList.map(
                  ({ data }) => data.uploadDraftFile._id
                ),
              },
            })
          : editPropertyNoteMutation({
              variables: {
                message,
                propertyNoteId: propertyNote._id,
                attachedFilesIds: [
                  ...(attachedFilesIds || []),
                  ...uploadedList.map(({ data }) => data.uploadDraftFile._id),
                ],
              },
            });
      })
      .then(() => {
        resetFormErrors();
        resetForm();
        setMessage(undefined);
        setFiles(undefined);
        return onCancel && onCancel();
      })
      .catch((error) => {
        setFileUploading(false);
        formOnError(translateServerResponse(error));
      });

    return null;
  };

  const onRemoveIconClick = useCallback(
    (fileId: string) => {
      const newFiles = files.filter(
        (fileItem: { _id: string }) => fileItem._id !== fileId
      );
      setFiles(newFiles);
      if (isEditMode) {
        setAttachedFilesIds(without(attachedFilesIds, fileId));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isEditMode, setAttachedFilesIds, attachedFilesIds, files]
  );

  const listItems = useMemo(() => {
    return (files || []).map((file: { _id: string; name: string }) => ({
      _id: file._id,
      name: isFilesUploading ? t('common:labels:loading') : file.name,
      onRemoveIconClick: onRemoveIconClick,
      isLoading: isFilesUploading,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files, onRemoveIconClick, isFilesUploading]);

  return (
    <Form w="100%" onSubmit={onSubmit}>
      <Form.Group mt={1}>
        <Form.Label required>{t`forms:labels:note`}</Form.Label>
        <Textarea
          w="100%"
          placeholder={t`forms:placeholders:note` || ''}
          onChange={(e) => {
            setMessage(e.target.value);
            validMessage(e.target.value);
          }}
          value={message || ''}
          $errorType={formErrors[FormFields.Message]?.type}
        />
        <Form.Feedback $errorType={formErrors[FormFields.Message]?.type}>
          {formErrors[FormFields.Message]?.message}
        </Form.Feedback>
      </Form.Group>
      <Form.Group>
        <FileList files={listItems} />
      </Form.Group>
      <Form.Group pt={4}>
        <Form.Feedback $errorType={FormErrorTypes.Invalid}>
          {formState.errorSlug}
        </Form.Feedback>
      </Form.Group>
      <Form.Group mt={3}>
        <Flex w="100%" justifyContent="space-between">
          <Flex>
            <Button
              medium
              isLoading={loading || updateLoading}
              disabled={loading || updateLoading}
              mr={2}
              type="submit"
            >
              {t('common:labels:save')}
            </Button>
            <Button
              medium
              variation="outline"
              onClick={() => {
                setMessage(undefined);
                setFiles(undefined);
              }}
            >
              {t('common:labels:cancel')}
            </Button>
          </Flex>
          <Flex>
            <FileInput
              medium
              type="button"
              multiple
              accept={Object.values(ALLOWED_MEDIA_TYPE_MIMES).join(',')}
              onFilesUpload={(files) => {
                const newItems = files.map((file) => ({
                  toUpload: file,
                  _id: RandomId(),
                  name: file.name,
                }));
                setFiles([...(formState.files || []), ...newItems]);
              }}
            />
          </Flex>
        </Flex>
      </Form.Group>
    </Form>
  );
}

export default SetNoteForm;
